export const getPageNumbers = (table, maxPagesToShow = 5) => {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  if (pageCount <= maxPagesToShow) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const pages = [];
  const sideCount = Math.floor((maxPagesToShow - 3) / 2);

  let start = Math.max(2, currentPage - sideCount);
  let end = Math.min(pageCount - 1, currentPage + sideCount);

  if (currentPage <= sideCount + 2) {
    start = 2;
    end = maxPagesToShow - 2;
  }

  if (currentPage >= pageCount - sideCount - 1) {
    start = pageCount - (maxPagesToShow - 3);
    end = pageCount - 1;
  }

  pages.push(1);

  if (start > 2) {
    pages.push('...');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < pageCount - 1) {
    pages.push('...');
  }

  pages.push(pageCount);

  return pages;
};

export const exportTableToCSV = (table, exportFields) => {
  if (!table) return;

  const rows = table.getFilteredRowModel().rows;

  if (rows.length === 0 || exportFields.length === 0) {
    alert('No rows or fields selected.');
    return;
  }

  // Detect system delimiter (comma or semicolon)
  const getSystemDelimiter = () => {
    const formatted = Intl.NumberFormat().format(1.1);
    return formatted.includes(',') ? ';' : ',';
  };

  const delimiter = getSystemDelimiter();

  const headers = exportFields.map((field) => table.getColumn(field)?.columnDef.header ?? field);

  const csvRows = [
    headers.join(delimiter),
    ...rows.map((row) =>
      exportFields
        .map((field) => {
          const value = row.getValue(field);
          if (typeof value === 'string') {
            return `"${value.replace(/"/g, '""')}"`; // Escape quotes
          }
          return value;
        })
        .join(delimiter)
    )
  ];

  const csvContent = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'export.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Flattens a nested object.
 * Nested keys are merged into top-level.
 * Optionally, you can provide a specific key to flatten.
 *
 * @param obj - The object to flatten
 * @param keyToFlatten - Optional: only flatten this key
 */
export const flattenObject = (obj, keyToFlatten) => {
  const result = { ...obj };

  const flatten = (nested) => {
    return Object.keys(nested || {}).reduce((acc, k) => {
      const val = nested[k];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return { ...acc, ...flatten(val) };
      }
      acc[k] = val;
      return acc;
    }, {});
  };

  if (keyToFlatten && result[keyToFlatten] && typeof result[keyToFlatten] === 'object') {
    // Only flatten the specified key
    const flattened = flatten(result[keyToFlatten]);

    return { ...result, ...flattened };
  }

  return result;
};

export const getColorForKeyword = (keyword, key, usedColors = new Set()) => {
  let hash = 0;

  const colors = [`${key}--green`, `${key}--yellow`, `${key}--orange`, `${key}--magenta`];

  for (let i = 0; i < keyword.length; i++) {
    hash = keyword.charCodeAt(i) + ((hash << 5) - hash);
  }

  let colorIndex = Math.abs(hash) % colors.length;
  let color = colors[colorIndex];

  // Ensure the second tag is different
  while (usedColors.has(color)) {
    colorIndex = (colorIndex + 1) % colors.length;
    color = colors[colorIndex];
  }

  usedColors.add(color);
  return color;
};
